// AI-CONTEXT: See docs/features/msi-effect-radar-chart.md
//
// Transposes useMatrixData's un-collapsed per-row array (`rowData`, the hook's
// internal `dataArray`) into radar-chart shape: one point per question, one key
// per measure moment. The values arrive ALREADY MIRRORED from the hook (negative
// rows flipped so higher = better), so this helper forwards them untouched — it
// must never re-apply mirroring (pitfall #database-raw-input-skips-negative-mirroring).
//
// Only questions that carry a numeric value at EVERY moment become an axis (the
// hook's "universalRows" rule) — otherwise two overlaid shapes would compare
// different sample sets across periods.

/**
 * @param {Array<object>} rowData - Per-moment rows from useMatrixData:
 *   `{ name: string, [rowTitle]: { value: number|null, negative?: boolean } | { counts } }`.
 * @param {Array<{ID: string, Title: string, posNeg?: string}>} [rawRowDefs] - Real
 *   MatrixQuestionRows defs; when given, axes are ordered by their Position order.
 * @returns {Array<Object<string, number|string>>} Radar points:
 *   `[{ question: string, [momentName]: number }, ...]`. Empty when no question is
 *   numeric at every moment.
 */
const buildRadarData = (rowData, rawRowDefs) => {
  const isNum = (v) => typeof v === 'number' && Number.isFinite(v)

  const moments = Array.isArray(rowData)
    ? rowData.filter((m) => m && typeof m === 'object')
    : []
  if (moments.length === 0) return []

  // Union of question keys across ALL moments — never assume moment[0] carries
  // every key (pitfall #ui-recharts-series-from-first-row).
  const unionKeys = []
  const seen = new Set()
  moments.forEach((moment) => {
    Object.keys(moment).forEach((key) => {
      if (key !== 'name' && !seen.has(key)) {
        seen.add(key)
        unionKeys.push(key)
      }
    })
  })

  // A question is an axis only if it has a numeric value at every moment.
  const universal = unionKeys.filter((key) =>
    moments.every((moment) => isNum(moment[key]?.value))
  )
  if (universal.length === 0) return []

  // Order axes by rawRowDefs (their Position order) when available; otherwise
  // keep the union order.
  let questions = universal
  if (Array.isArray(rawRowDefs) && rawRowDefs.length) {
    const rank = new Map(
      rawRowDefs.map((row, index) => [row?.Title, index]).filter(([title]) => title)
    )
    questions = [...universal].sort((a, b) => {
      const ra = rank.has(a) ? rank.get(a) : Infinity
      const rb = rank.has(b) ? rank.get(b) : Infinity
      return ra - rb
    })
  }

  return questions.map((question) => {
    const point = { question }
    moments.forEach((moment) => {
      point[moment.name] = moment[question].value
    })
    return point
  })
}

export default buildRadarData
